import React from 'react'
import styled from 'styled-components'

interface Props {
  label: React.ReactNode
  value?: React.ReactNode
  description?: React.ReactNode
  badge?: React.ReactNode
  placeholder?: string
  layout?: 'stack' | 'row'
}

export const SettingsReadOnlyField: React.FC<Props> = ({
  label,
  value,
  description,
  badge,
  placeholder = '-',
  layout = 'stack'
}) => {
  const hasValue =
    value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '')

  return (
    <Wrapper $layout={layout}>
      <Copy>
        <LabelRow>
          <Label $layout={layout}>{label}</Label>
          {badge ? <Badge>{badge}</Badge> : null}
        </LabelRow>
        {description ? <Description>{description}</Description> : null}
      </Copy>
      <Value $isPlaceholder={!hasValue} $layout={layout}>
        {hasValue ? value : placeholder}
      </Value>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $layout: 'stack' | 'row' }>`
  display: flex;
  flex-direction: ${({ $layout }) => ($layout === 'row' ? 'row' : 'column')};
  align-items: ${({ $layout }) => ($layout === 'row' ? 'flex-start' : 'stretch')};
  gap: ${({ theme, $layout }) =>
    $layout === 'row' ? `${theme.spacing.md}rem` : `${theme.spacing.xxxs}rem`};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
`

const Copy = styled.div`
  flex: 1;
  min-width: 0;
`

const LabelRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  flex-wrap: wrap;
`

const Label = styled.span<{ $layout: 'stack' | 'row' }>`
  font-size: ${({ theme, $layout }) =>
    $layout === 'row' ? `${theme.fontSize.md}rem` : `${theme.fontSize.sm}rem`};
  font-weight: ${({ $layout }) => ($layout === 'row' ? 500 : 400)};
  color: ${({ theme, $layout }) =>
    $layout === 'row' ? theme.colors.menuBg : theme.dashboard.textSubtle};
`

const Description = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  text-wrap: pretty;
`

const Badge = styled.span`
  padding: ${({ theme }) => theme.spacing.xxxs}rem ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.dashboard.textSubtle};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
`

const Value = styled.div<{ $isPlaceholder: boolean; $layout: 'stack' | 'row' }>`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme, $isPlaceholder }) =>
    $isPlaceholder ? theme.dashboard.textSubtle : theme.colors.menuBg};
  font-weight: ${({ $isPlaceholder }) => ($isPlaceholder ? 400 : 500)};
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  flex: ${({ $layout }) => ($layout === 'row' ? '0 0 auto' : 'initial')};
`
